import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import {
  buildStatusMetricCards,
  mapStatusOptionsToMetricOptions,
  resolveMetricTone,
} from '@/features/metrics/components/statusMetricCards';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { buildReleaseCounts } from '@/features/quality/components/qualityStatusDataHelpers';
import { useQualityDateRange, type QualityRange } from '@/features/quality/hooks';
import { queryReleaseSummary } from '@/features/releases/data';
import { useScopedProjectFilter } from '@/features/shared/hooks/useScopedProjectFilter';
import { buildDeliveryPageFilters } from '@/features/shared/utils/deliveryPageFilters';
import { releaseStatusOptions } from '@/shared/constants/statusOptions';
import {
  queryKeys,
  useIsAuthenticated,
  useOrganisationEntitlements,
  usePersistedStringArray,
  useTagOptions,
} from '@/shared/hooks';
import { buildInFilter } from '@/ui/composites/grid/gridFilters';
import { buildGridQueryInput, type GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { ReleaseStatus } from '@flowqa/shared';

type UseReleasesPageViewModelResult = {
  range: QualityRange;
  setRange: (value: QualityRange) => void;
  selectedStatuses: ReleaseStatus[];
  selectedTags: string[];
  selectedProjects: string[];
  tagOptions: Array<{ value: string; label: string }>;
  tagOptionsLoading: boolean;
  projectFilterOptions: Array<{ value: string; label: string }>;
  showProjectFilter: boolean;
  onStatusToggle: (status: ReleaseStatus) => void;
  onStatusesChange: (nextStatuses: ReleaseStatus[]) => void;
  onTagsChange: (nextTags: string[]) => void;
  onProjectsChange: (nextProjects: string[]) => void;
  tableFilters: GridQueryInput['filters'];
  releaseMetricCards: ReturnType<typeof buildStatusMetricCards>;
  dateFiltersEnabled: boolean;
};

export const useReleasesPageViewModel = (): UseReleasesPageViewModelResult => {
  const isAuthenticated = useIsAuthenticated();
  const { selectedOrganisationId } = useOrganisationFilter();
  const {
    effectiveProjectId,
    handleProjectsChange,
    projectFilterOptions,
    selectedProjects,
    showProjectFilter,
  } = useScopedProjectFilter({ enabled: selectedOrganisationId !== null });
  const [range, setRangeState] = useState<QualityRange>('7d');
  const [selectedStatuses, setSelectedStatuses] = useState<ReleaseStatus[]>([]);
  const [selectedTags, setSelectedTags] = usePersistedStringArray('tag-filter:releases');
  const { tagOptions, loading: tagOptionsLoading } = useTagOptions({
    projectId: effectiveProjectId,
    enabled: isAuthenticated,
  });
  const { rangeStartIso, rangeEndIso } = useQualityDateRange(range);
  const entitlementsQuery = useOrganisationEntitlements(selectedOrganisationId ?? null);
  const deliveryReady = selectedOrganisationId === null || entitlementsQuery.isSuccess;
  const deliveryEnabled =
    selectedOrganisationId === null
      ? true
      : (entitlementsQuery.data?.addOns.deliveryEnabled ?? false);
  const dateFiltersEnabled = true;
  const showDelta = false;

  const tagFilters = useMemo(() => buildInFilter('tags', selectedTags), [selectedTags]);
  const projectFilters = useMemo(
    () => (showProjectFilter ? buildInFilter('projectId', selectedProjects) : []),
    [selectedProjects, showProjectFilter],
  );
  const statusFilters = useMemo(
    () => buildInFilter('status', selectedStatuses),
    [selectedStatuses],
  );
  const { summaryFilters } = useMemo(
    () =>
      buildDeliveryPageFilters({
        rangeStartIso,
        rangeEndIso,
        previousRangeStartIso: null,
        previousRangeEndIso: null,
        statusFilters,
        tagFilters,
        projectFilters,
      }),
    [projectFilters, rangeEndIso, rangeStartIso, statusFilters, tagFilters],
  );
  const releaseSummaryInput = useMemo(
    () =>
      buildGridQueryInput({
        page: 1,
        pageSize: 1,
        sorts: [],
        filters: summaryFilters,
      }),
    [summaryFilters],
  );
  const releaseSummaryFiltersKey = useMemo(
    () => JSON.stringify(releaseSummaryInput.filters),
    [releaseSummaryInput.filters],
  );
  const releaseSummaryQuery = useQuery({
    queryKey: queryKeys.releaseSummary(
      effectiveProjectId,
      selectedOrganisationId ?? null,
      releaseSummaryFiltersKey,
    ),
    enabled: isAuthenticated && deliveryReady && deliveryEnabled,
    queryFn: async () =>
      queryReleaseSummary(
        effectiveProjectId,
        selectedOrganisationId ?? undefined,
        releaseSummaryInput,
      ),
  });
  const releaseSummary = releaseSummaryQuery.data ?? null;
  const releaseCounts = useMemo(() => buildReleaseCounts(releaseSummary), [releaseSummary]);
  const releaseMetricOptions = useMemo(
    () => mapStatusOptionsToMetricOptions(releaseStatusOptions, resolveMetricTone),
    [],
  );
  const releaseMetricCards = useMemo(
    () =>
      buildStatusMetricCards({
        options: releaseMetricOptions,
        counts: releaseCounts,
        deltas: undefined,
        total: releaseSummary?.total,
        totalLabel: 'Releases',
        previousCounts: {},
        previousTotal: undefined,
        loading: releaseSummaryQuery.isLoading,
        showDelta,
      }),
    [
      releaseCounts,
      releaseMetricOptions,
      releaseSummary?.total,
      releaseSummaryQuery.isLoading,
      showDelta,
    ],
  );
  const tableFilters = summaryFilters;
  const onStatusToggle = useCallback((status: ReleaseStatus) => {
    setSelectedStatuses((prev) =>
      prev.includes(status) ? prev.filter((item) => item !== status) : [...prev, status],
    );
  }, []);
  const onStatusesChange = useCallback((nextStatuses: ReleaseStatus[]) => {
    setSelectedStatuses(nextStatuses);
  }, []);

  const setRange = useCallback((value: QualityRange) => {
    setRangeState(value);
  }, []);

  return {
    range,
    setRange,
    selectedStatuses,
    selectedTags,
    selectedProjects,
    tagOptions,
    tagOptionsLoading,
    projectFilterOptions,
    showProjectFilter,
    onStatusToggle,
    onStatusesChange,
    onTagsChange: setSelectedTags,
    onProjectsChange: handleProjectsChange,
    tableFilters,
    releaseMetricCards,
    dateFiltersEnabled,
  };
};
